<template>
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
      <!-- Header -->
      <div class="flex justify-between items-center p-6 border-b border-gray-200">
        <h3 class="text-xl font-bold text-gray-900">
          {{ isEditing ? 'Edit Balance Bank' : 'Add Balance Bank' }}
        </h3>
        <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600">
          <X class="w-6 h-6" />
        </button>
      </div>

      <!-- Form -->
      <form @submit.prevent="handleSubmit" class="p-6 space-y-6">
        <!-- Code -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Bank Code *
            <span class="text-xs text-gray-500">(Unique identifier, e.g., CASUAL)</span>
          </label>
          <input
            v-model="formData.code"
            type="text"
            required
            :disabled="isEditing && bank?.is_system_defined"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            placeholder="e.g., CUSTOM_BANK"
          />
        </div>

        <!-- Name -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Bank Name *
          </label>
          <input
            v-model="formData.name"
            type="text"
            required
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., Custom Leave Bank"
          />
        </div>

        <!-- Description -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            v-model="formData.description"
            rows="3"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Describe the purpose of this balance bank..."
          ></textarea>
        </div>

        <!-- Calculation Type -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Calculation Type *
          </label>
          <select
            v-model="formData.calculation_type"
            required
            :disabled="isEditing && bank?.is_system_defined"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
          >
            <option value="">Select calculation type</option>
            <option value="tenure_based">Tenure-Based (varies by years of service)</option>
            <option value="fixed">Fixed (same for all employees)</option>
            <option value="manual">Manual (admin configures per employee)</option>
          </select>
          <p class="text-xs text-gray-500 mt-1">
            Determines how balance is allocated to employees
          </p>
        </div>

        <!-- Fixed Days (only for fixed type) -->
        <div v-if="formData.calculation_type === 'fixed'">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Fixed Days *
          </label>
          <input
            v-model.number="formData.fixed_days"
            type="number"
            step="0.5"
            min="0"
            required
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., 14"
          />
          <p class="text-xs text-gray-500 mt-1">
            Number of days allocated to all employees
          </p>
        </div>

        <!-- Carryover Settings -->
        <div class="border-t border-gray-200 pt-6">
          <h4 class="text-sm font-medium text-gray-900 mb-4">Carryover Settings</h4>

          <div class="flex items-center mb-4">
            <input
              v-model="formData.carryover_enabled"
              type="checkbox"
              id="carryover"
              class="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <label for="carryover" class="ml-2 text-sm text-gray-700">
              Allow unused days to carry over to next year
            </label>
          </div>

          <div v-if="formData.carryover_enabled">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Maximum Carryover Days
            </label>
            <input
              v-model.number="formData.max_carryover"
              type="number"
              step="0.5"
              min="0"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., 7"
            />
            <p class="text-xs text-gray-500 mt-1">
              Maximum days that can be carried to next year (leave empty for unlimited)
            </p>
          </div>
        </div>

        <!-- Display Settings -->
        <div class="border-t border-gray-200 pt-6">
          <h4 class="text-sm font-medium text-gray-900 mb-4">Display Settings</h4>

          <!-- Color Picker -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Color
            </label>
            <div class="flex items-center gap-2">
              <input
                v-model="formData.color"
                type="color"
                class="w-12 h-10 border border-gray-300 rounded cursor-pointer"
              />
              <input
                v-model="formData.color"
                type="text"
                class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="#3B82F6"
              />
            </div>
          </div>

          <!-- Display Order -->
          <div class="mt-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Display Order
            </label>
            <input
              v-model.number="formData.display_order"
              type="number"
              min="0"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="0"
            />
            <p class="text-xs text-gray-500 mt-1">
              Lower numbers appear first
            </p>
          </div>

          <!-- Active Status -->
          <div class="mt-4 flex items-center">
            <input
              v-model="formData.is_active"
              type="checkbox"
              id="is_active"
              class="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <label for="is_active" class="ml-2 text-sm text-gray-700">
              Active (visible to employees)
            </label>
          </div>
        </div>

        <!-- Error Message -->
        <div v-if="error" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {{ error }}
        </div>

        <!-- Actions -->
        <div class="flex justify-end gap-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            @click="$emit('close')"
            class="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            :disabled="submitting"
            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <span v-if="submitting">Saving...</span>
            <span v-else>{{ isEditing ? 'Update Bank' : 'Create Bank' }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { X } from 'lucide-vue-next';
import balanceBankService from '@/services/balanceBankService';

const props = defineProps({
  bank: {
    type: Object,
    default: null
  }
});

const emit = defineEmits(['close', 'saved']);

const isEditing = computed(() => !!props.bank);
const submitting = ref(false);
const error = ref(null);

const formData = ref({
  code: '',
  name: '',
  description: '',
  calculation_type: '',
  fixed_days: null,
  carryover_enabled: false,
  max_carryover: null,
  color: '#3B82F6',
  display_order: 0,
  is_active: true
});

onMounted(() => {
  if (props.bank) {
    formData.value = {
      code: props.bank.code,
      name: props.bank.name,
      description: props.bank.description || '',
      calculation_type: props.bank.calculation_type,
      fixed_days: props.bank.fixed_days,
      carryover_enabled: props.bank.carryover_enabled === 1,
      max_carryover: props.bank.max_carryover,
      color: props.bank.color || '#3B82F6',
      display_order: props.bank.display_order || 0,
      is_active: props.bank.is_active !== 0
    };
  }
});

async function handleSubmit() {
  submitting.value = true;
  error.value = null;

  try {
    const data = {
      ...formData.value,
      carryover_enabled: formData.value.carryover_enabled ? 1 : 0,
      is_active: formData.value.is_active ? 1 : 0
    };

    if (isEditing.value) {
      await balanceBankService.updateBank(props.bank.id, data);
    } else {
      await balanceBankService.createBank(data);
    }

    emit('saved');
  } catch (err) {
    console.error('Error saving bank:', err);
    error.value = err.response?.data?.message || 'Failed to save balance bank';
  } finally {
    submitting.value = false;
  }
}
</script>
